import React, { useEffect, useMemo, useState } from 'react';
import {
    Buildings,
    CheckCircle,
    Circle,
    ClipboardText,
    CreditCard,
    CurrencyCircleDollar,
    FileText,
    Files,
    Package,
    Scroll,
    Sun,
    TreeStructure,
    Wallet,
    BatteryHigh,
    House,
} from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import quoteServices from '../../../../../services/oneButtonServices/quoteServices';
import stripeServices, { summarizeStripeLinkStatus } from '../../../../../services/oneButtonServices/stripeServices';
import opportunityInfoServices from '../../../../../services/oneButtonServices/opportunityInfoService';
import { getOneButtonCapabilityValue } from '../../../../../services/oneButtonServices/capabilityService';
import { SalesRepChecklistEngine } from '../SalesRepChecklist/salesrepChecklistEngine';
import { isQuoteContractRowSigned, useFinanceAgreementMilestone } from '../financeAgreementMilestone';
import { mapQuoteProductTypeLongNameToCode, normPartnerKey } from '../contractFlowHelpers';
import {
    showStripeFinancePartnerSyncModal,
    stripeFinancePartnerSyncMessage,
} from '../../../../../helpers/stripeFinancePartnerSyncGate';

export type ObdMilestoneCardDef = {
    id: string;
    label: string;
    sublabel?: string;
    complete?: boolean;
    /** Stripe (and similar): paid green, some paid orange, none paid red. */
    badgeTone?: 'paid' | 'partial' | 'unpaid';
    badgeColor?: string;
    hideTopRightBadge?: boolean;
    icon: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
    badges?: React.ReactNode;
};

export type ChecklistResponseToneData = {
    opportunityId?: number;
    responseTone?: string;
    responseColorCode?: string;
};

export function getChecklistToneColor(toneData?: ChecklistResponseToneData | null): string | null {
    if (!toneData) return null;
    const item = Array.isArray(toneData) ? toneData[0] : toneData;
    const colorCode = item?.responseColorCode;
    return colorCode ? String(colorCode).trim() : null;
}



export type UseStandardMilestoneCardsArgs = {
    opportunityNumericId: string | number | null | undefined;
    salesForceId?: string | null;
    savedProducts?: any[];
    financePartnerSyncStatus?: any;
};

export type MilestoneBadgeGroup = {
    code: string;
    label: string;
    products: any[];
    partnerKey: string;
    methodKey: string;
    sortIndex: number;
};

export function badgeLayoutClassForCode(code: string): string {
    return ['SB', 'SR', 'SBR', 'BR'].includes(code) ? 'obd-v3-milestone-card__prod-badge--wide' : '';
}

const MILESTONE_SORT_ORDER = ['solar', 'roof', 'battery', 'rr', 'rrr'];

const PRODUCT_LABELS_BY_CODE: Record<string, string> = {
    S: 'Solar',
    B: 'Battery',
    R: 'Roof',
    RO: 'Roof',
    BO: 'Battery',
    SB: 'Solar + Battery',
    SR: 'Solar + Roof',
    SBR: 'Solar + Battery + Roof',
    BR: 'Battery + Roof',
    RR: 'RR',
    RRR: 'RRR',
};

function getActiveMilestoneProducts(propSavedProducts: any[] | undefined, opportunityDetails: any): any[] {
    if (Array.isArray(propSavedProducts) && propSavedProducts.length > 0) return propSavedProducts;
    if (!opportunityDetails) return [];
    if (Array.isArray(opportunityDetails.opportunityProduct)) return opportunityDetails.opportunityProduct;
    if (Array.isArray(opportunityDetails.OpportunityProduct)) return opportunityDetails.OpportunityProduct;
    if (Array.isArray(opportunityDetails.opportunityProductList)) return opportunityDetails.opportunityProductList;
    return [];
}

function normalizedProductName(product: any): string {
    return String(product?.Product_Type_Name ?? product?.product_Type_Name ?? '')
        .trim()
        .toLowerCase();
}

function milestoneProductSortIndex(product: any): number {
    const idx = MILESTONE_SORT_ORDER.indexOf(normalizedProductName(product));
    return idx === -1 ? 999 : idx;
}

function productCodeForBadgeGroup(products: any[]): string {
    const names = new Set(products.map(normalizedProductName));
    const hasSolar = names.has('solar');
    const hasBattery = names.has('battery');
    const hasRoof = names.has('roof') || names.has('rr') || names.has('rrr');
    const onlyRoof = names.size === 1 && hasRoof;

    if (hasSolar && hasBattery && hasRoof) return 'SBR';
    if (hasSolar && hasBattery) return 'SB';
    if (hasSolar && hasRoof) return 'SR';
    if (hasBattery && hasRoof) return 'BR';
    if (hasSolar) return 'S';
    if (hasBattery) return 'B';
    if (onlyRoof && names.has('rrr')) return 'RRR';
    if (onlyRoof && names.has('rr')) return 'RR';
    if (hasRoof) return 'RO';
    return String(products[0]?.ProductType ?? products[0]?.Product_Type_Name ?? '').trim().toUpperCase() || 'S';
}

export function buildMilestoneBadgeGroups(activeProducts: any[]): MilestoneBadgeGroup[] {
    if (!Array.isArray(activeProducts) || activeProducts.length === 0) return [];

    const sortedProducts = [...activeProducts].sort((a, b) => milestoneProductSortIndex(a) - milestoneProductSortIndex(b));
    const groups = new Map<string, any[]>();

    sortedProducts.forEach((product) => {
        const partnerKey = normPartnerKey(
            String(product?.Finance_Partner_Display_Name ?? product?.Finance_Partner ?? product?.finance_Partner ?? ''),
        );
        const methodKey = String( !!product?.Finance_Method_Name? product?.Finance_Method_Name : product?.FinanceMethod ?? product?.Finance_Partner ?? '')
            .trim()
            .toLowerCase();
        const key = `${partnerKey}::${methodKey}`;
        const list = groups.get(key) ?? [];
        list.push(product);
        groups.set(key, list);
    });

    return Array.from(groups.entries())
        .map(([groupKey, products]) => {
            const [partnerKey, methodKey] = groupKey.split('::');
            const code = productCodeForBadgeGroup(products);
            const label = PRODUCT_LABELS_BY_CODE[code] || String(products[0]?.Product_Type_Name ?? '').trim() || 'Product';
            return {
                code,
                label,
                products,
                partnerKey,
                methodKey,
                sortIndex: Math.min(...products.map(milestoneProductSortIndex)),
            };
        })
        .sort((a, b) => a.sortIndex - b.sortIndex);
}

function contractStatusCode(status: any): string {
    const raw = String(status?.Product ?? status?.ProductType ?? '').trim();
    const mapped = mapQuoteProductTypeLongNameToCode(raw);
    return String(mapped || raw).trim().toUpperCase();
}

function partnerMatchesStatus(group: MilestoneBadgeGroup, status: any): boolean {
    const statusPartner = normPartnerKey(String(status?.FinancePartner ?? status?.financePartner ?? ''));
    return !group.partnerKey || !statusPartner || group.partnerKey === statusPartner;
}

function findStatusesForBadgeGroup(group: MilestoneBadgeGroup, contractStatuses: any[]): any[] {
    const statuses = Array.isArray(contractStatuses) ? contractStatuses : [];
    const exactGroupMatches = statuses.filter(
        (status) => contractStatusCode(status) === group.code && partnerMatchesStatus(group, status),
    );
    if (exactGroupMatches.length > 0) return exactGroupMatches;

    const perProductMatches = group.products
        .map((product) => {
            const productMatches = findMatchingContractStatuses(product, statuses);
            const partnerScopedMatch = productMatches.find((status) => partnerMatchesStatus(group, status));
            return partnerScopedMatch ?? productMatches[0] ?? null;
        })
        .filter(Boolean);

    return Array.from(new Set(perProductMatches));
}

function findMatchingContractStatus(product: any, contractStatuses: any[]) {
    return findMatchingContractStatuses(product, contractStatuses)[0];
}

function findMatchingContractStatuses(product: any, contractStatuses: any[]) {
    const productName = String(product?.Product_Type_Name ?? product?.product_Type_Name ?? '')
        .trim()
        .toLowerCase();
    const productTypeId = Number(product?.Product_Type_Id ?? product?.product_Type_Id ?? 0);

    return (Array.isArray(contractStatuses) ? contractStatuses : []).filter((status: any) => {
        const statusCode = String(status?.Product ?? status?.ProductType ?? '')
            .trim()
            .toLowerCase();
        const quoteGroupId = Number(status?.QuoteGroupId ?? status?.quoteGroupId ?? 0);
        const hasExplicitStatusCode = statusCode.length > 0;

        if (productTypeId === 1 || productName.includes('battery')) {
            if (hasExplicitStatusCode) {
                return statusCode === 'b' || statusCode === 'bo' || statusCode.includes('battery');
            }
            return quoteGroupId === 1;
        }

        if (productTypeId === 2 || productName.includes('roof') || productName === 'rr' || productName === 'rrr') {
            if (hasExplicitStatusCode) {
                return statusCode === 'r' || statusCode === 'ro' || statusCode.includes('roof');
            }
            return quoteGroupId === 2;
        }

        if (productTypeId === 3 || productName.includes('solar')) {
            if (hasExplicitStatusCode) {
                return statusCode === 's' || statusCode.includes('solar');
            }
            return quoteGroupId === 3;
        }

        return statusCode === productName;
    });
}

function isCombinedPackAgreementSigned(status: any): boolean {
    const details = status?.AgreementDetails ?? status?.agreementDetails ?? null;
    if (Array.isArray(details)) {
        return details.some((detail) => String(detail?.Status ?? detail?.status ?? '').trim().toUpperCase() === 'SIGNED');
    }
    if (details && typeof details === 'object') {
        return String((details as any)?.Status ?? (details as any)?.status ?? '')
            .trim()
            .toUpperCase() === 'SIGNED';
    }
    return false;
}

export function useStandardMilestoneCards({
    opportunityNumericId,
    salesForceId,
    savedProducts: propSavedProducts,
    financePartnerSyncStatus: propFinancePartnerSyncStatus,
}: UseStandardMilestoneCardsArgs): ObdMilestoneCardDef[] {
    const checkListDetails = useOneButtonStore((s) => s.checkListDetails);
    const salesRepChecklistProgress = useOneButtonStore((s) => s.salesRepChecklistProgress);
    const setSalesRepChecklistProgress = useOneButtonStore((s) => s.setSalesRepChecklistProgress);
    const checklistResponseTone = useOneButtonStore((s) => s.checklistResponseTone);
    const setChecklistResponseTone = useOneButtonStore((s) => s.setChecklistResponseTone);
    const setSalesRepChecklistModalOpen = useOneButtonStore((s) => s.setSalesRepChecklistModalOpen);
    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const salesRepChecklistAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'SalesRepCheckListEnable');
    const salesRepChecklistEnabled =
        salesRepChecklistAccess === 'View' ||
        salesRepChecklistAccess === 'Edit' ||
        salesRepChecklistAccess === 'Yes' ||
        // oneDRAW capability payloads may omit this Angular-only flag; still allow the dashboard entry.
        salesRepChecklistAccess === '';
    const documentsAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Documents_Access');
    const documentsAccessEnabled = documentsAccess === 'Edit';
    const setQuotesOpen = useOneButtonStore((s) => s.setCommerceHubQuotesOpen);
    const setAgreementsOpen = useOneButtonStore((s) => s.setCommerceHubAgreementsOpen);
    const setStripeOpen = useOneButtonStore((s) => s.setCommerceHubStripeOpen);
    const stripeOpen = useOneButtonStore((s) => s.commerceHubStripeOpen);
    const setDocumentsModalOpen = useOneButtonStore((s) => s.setDocumentsModalOpen);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const financePartnersList = useOneButtonStore((s) => s.financePartnersList);
    const theme = useOneButtonStore((s) => s.theme);
    const storeContractCreationStatuses = useOneButtonStore((s) => s.contractCreationStatuses);
    const financeAgreement = useFinanceAgreementMilestone(opportunityNumericId);
    const [stripeStatus, setStripeStatus] = useState<{
        sublabel: string;
        complete: boolean;
        badgeTone?: 'paid' | 'partial' | 'unpaid';
    }>({ sublabel: 'Payments', complete: false });
    const hasStripeAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'Stripe_Access') === 'Edit';

    useEffect(() => {
        if (!opportunityNumericId) return;
        void quoteServices.getPageChanges(opportunityNumericId).catch(() => undefined);
        if (!salesRepChecklistEnabled) return;
        void opportunityInfoServices
            .retrieveSalesRepChecklistData(opportunityNumericId)
            .then((data) => {
                const engine = new SalesRepChecklistEngine(() => undefined);
                engine.hydrateFromApi(data);
                setSalesRepChecklistProgress(engine.getOverallStats());
            })
            .catch(() => undefined);
        void opportunityInfoServices
            .getChecklistResponseTone(opportunityNumericId)
            .then((toneData) => {
                const payload = toneData?.data ?? toneData;
                if (payload && typeof payload === 'object') {
                    setChecklistResponseTone(payload);
                }
            })
            .catch(() => undefined);
    }, [opportunityNumericId, salesRepChecklistEnabled, setSalesRepChecklistProgress, setChecklistResponseTone]);


    useEffect(() => {
        if (!hasStripeAccess) return;
        const sf = String(salesForceId || '').trim();
        if (!sf || !opportunityNumericId) return;
        if (stripeOpen) return;
        void stripeServices
            .getStripePaymentsLinks(0, 0, sf, 'oneBUTTON')
            .then((payload) => setStripeStatus(summarizeStripeLinkStatus(payload.payment_Link_Details)))
            .catch(() => setStripeStatus({ sublabel: 'Payments', complete: false }));
    }, [hasStripeAccess, opportunityNumericId, salesForceId, stripeOpen]);

    const checklistProgress = useMemo(() => {
        if (salesRepChecklistProgress && salesRepChecklistProgress.total > 0) {
            return {
                done: salesRepChecklistProgress.answered,
                total: salesRepChecklistProgress.total,
            };
        }
        const rows = Array.isArray(checkListDetails) ? checkListDetails : [];
        const total = rows.length > 0 ? rows.length : 0;
        const done = rows.filter((r) => {
            const changes = String(r?.Changes ?? '').toLowerCase();
            return changes.includes('complete') || changes.includes('signed') || changes.includes('enabled');
        }).length;
        return { done, total };
    }, [checkListDetails, salesRepChecklistProgress]);

    const milestoneBadgeGroups = useMemo(
        () => buildMilestoneBadgeGroups(getActiveMilestoneProducts(propSavedProducts, opportunityDetails)),
        [propSavedProducts, opportunityDetails],
    );

    const salesforceBadgesAndStatus = useMemo(() => {
        let allGreen = true;
        let badges: React.ReactNode = undefined;
        let complete = false;
        if (milestoneBadgeGroups.length > 0) {
            const contractStatuses = Array.isArray(storeContractCreationStatuses) 
                ? storeContractCreationStatuses 
                : [];

            badges = milestoneBadgeGroups.map((group, i: number) => {
                const matchedStatuses = findStatusesForBadgeGroup(group, contractStatuses);
                const isSyncComplete =
                    matchedStatuses.length > 0 &&
                    matchedStatuses.find((matchedStatus) => {
                        const leadSync = String(matchedStatus?.SFDC_Lead_Is_Sync).toLowerCase() === 'true';
                        const contactSync = String(matchedStatus?.SFDC_Contact_Is_Sync).toLowerCase() === 'true';
                        const contractSync = String(matchedStatus?.SFDC_Contract_Is_Sync).toLowerCase() === 'true';
                        return leadSync && contactSync && contractSync;
                    }); 

                if (!isSyncComplete) allGreen = false;

                const color = isSyncComplete ? 'green' : 'orange';
                const statusTooltip = isSyncComplete ? 'Completed' : 'Pending';

                return (
                    <span
                        key={i}
                        title={statusTooltip}
                        className={`obd-v3-milestone-card__prod-badge ${badgeLayoutClassForCode(group.code)}`.trim()}>
                        {group.label}
                        <span className={`obd-v3-milestone-card__prod-indicator obd-v3-milestone-card__prod-indicator--${color}`} />
                    </span>
                );
            });
            complete = allGreen;
        }

        return { badges, complete, sublabel: complete ? 'Completed' : 'CRM sync' };
    }, [milestoneBadgeGroups, storeContractCreationStatuses]);

    const combinedPackBadgesAndStatus = useMemo(() => {
        let allGreen = true;
        let badges: React.ReactNode = undefined;
        let complete = false;
        if (milestoneBadgeGroups.length > 0) {
            const contractStatuses = Array.isArray(financeAgreement?.quoteContracts)
                ? financeAgreement.quoteContracts
                : [];

            badges = milestoneBadgeGroups.map((group, i: number) => {
                const matchedStatuses = findStatusesForBadgeGroup(group, contractStatuses);
                const isSigned =
                    matchedStatuses.length > 0 &&
                    matchedStatuses.find((matchedStatus) => isCombinedPackAgreementSigned(matchedStatus));

                if (!isSigned) allGreen = false;

                const color = isSigned ? 'green' : 'orange';
                const statusTooltip = isSigned ? 'Completed' : 'Pending';

                return (
                    <span
                        key={i}
                        title={statusTooltip}
                        className={`obd-v3-milestone-card__prod-badge ${badgeLayoutClassForCode(group.code)}`.trim()}>
                        {group.label}
                        <span className={`obd-v3-milestone-card__prod-indicator obd-v3-milestone-card__prod-indicator--${color}`} />
                    </span>
                );
            });
            complete = allGreen;
        }

        return { badges, complete, sublabel: complete ? 'Completed' : 'Pack agreement' };
    }, [milestoneBadgeGroups, financeAgreement]);

    const financeBadgesAndStatus = useMemo(() => {
        let allGreen = true;
        let badges: React.ReactNode[] = [];
        let complete = false;
        let signedBadgeCount = 0;
        if (milestoneBadgeGroups.length > 0) {
            const contractStatuses = financeAgreement?.quoteContracts || [];
            let validBadgeCount = 0;

            milestoneBadgeGroups.forEach((group, i: number) => {
                const matchedStatuses = findStatusesForBadgeGroup(group, contractStatuses);
                const hasCashProduct = group.products.some((product) => {
                    const partnerNameFromProduct = String(
                        product?.Finance_Partner_Display_Name || product?.Finance_Partner || product?.finance_Partner || '',
                    )
                        .trim()
                        .toLowerCase();
                    return partnerNameFromProduct === 'cash';
                });
                if (hasCashProduct) {
                    return;
                }
                validBadgeCount++;
                const isSigned =
                    matchedStatuses.length > 0 &&
                    matchedStatuses.find((status) => isQuoteContractRowSigned(status as Record<string, unknown>));

                if (!isSigned) allGreen = false;
                else signedBadgeCount++;

                const color = isSigned ? 'green' : 'orange';
                const statusTooltip = isSigned ? 'Completed' : 'Pending';

                badges.push(
                    <span
                        key={`fin-${i}`}
                        title={statusTooltip}
                        className={`obd-v3-milestone-card__prod-badge ${badgeLayoutClassForCode(group.code)}`.trim()}>
                        {group.label}
                        <span className={`obd-v3-milestone-card__prod-indicator obd-v3-milestone-card__prod-indicator--${color}`} />
                    </span>
                );
            });

            complete = validBadgeCount > 0 ? allGreen : financeAgreement.complete;

            let sublabel = financeAgreement.sublabel;
            if (validBadgeCount > 0) {
                if (signedBadgeCount === validBadgeCount) sublabel = 'Signed';
                else if (signedBadgeCount > 0) sublabel = 'Partially signed';
                else sublabel = 'Pending signature';
            }

            return {
                badges: badges.length > 0 ? badges : undefined,
                complete: milestoneBadgeGroups.length > 0 && badges.length > 0 ? complete : financeAgreement.complete,
                sublabel,
            };
        }

        return {
            badges: badges.length > 0 ? badges : undefined,
            complete: milestoneBadgeGroups.length > 0 && badges.length > 0 ? complete : financeAgreement.complete,
            sublabel: financeAgreement.sublabel,
        };
    }, [milestoneBadgeGroups, financeAgreement]);

    return useMemo(
        () => {
            const checklistToneColor = getChecklistToneColor(checklistResponseTone);
            const cards: ObdMilestoneCardDef[] = [
            {
                id: 'checklist',
                label: 'Checklist',
                sublabel:
                    checklistProgress.total > 0
                        ? `${checklistProgress.done}/${checklistProgress.total}`
                        : salesRepChecklistEnabled
                        ? 'Open'
                        : 'Unavailable',
                complete: checklistProgress.done >= checklistProgress.total && checklistProgress.total > 0,
                badgeColor: checklistToneColor || undefined,
                icon: <ClipboardText size={28} weight="duotone" />,
                onClick: () => setSalesRepChecklistModalOpen(true),
                disabled: !salesRepChecklistEnabled,
            },
            {
                id: 'finance',
                label: 'Finance contract',
                sublabel: financeBadgesAndStatus.sublabel,
                complete: financeBadgesAndStatus.complete,
                icon: <Scroll size={28} weight="duotone" />,
                badges: financeBadgesAndStatus.badges,
                onClick: () => {
                    setQuotesOpen(false);
                    setAgreementsOpen(true);
                },
            },
            {
                id: 'salesforce',
                label: 'Salesforce',
                sublabel: salesforceBadgesAndStatus.sublabel,
                complete: salesforceBadgesAndStatus.complete,
                icon: <Buildings size={28} weight="duotone" />,
                badges: salesforceBadgesAndStatus.badges,
                onClick: () => {
                    const sf = salesForceId?.trim();
                    if (sf) window.open(`${import.meta.env.VITE_SALESFORCE_URL}${sf}/view`, '_blank');
                },
            },
            {
                id: 'combined',
                label: 'Combined pack',
                sublabel: combinedPackBadgesAndStatus.sublabel,
                complete: combinedPackBadgesAndStatus.complete,
                icon: <Package size={28} weight="duotone" />,
                badges: combinedPackBadgesAndStatus.badges,
                onClick: () => setAgreementsOpen(true),
            },
            {
                id: 'm1',
                label: 'Milestone 1',
                sublabel: 'Site survey',
                icon: <TreeStructure size={28} weight="duotone" />,
                onClick: () => setQuotesOpen(true),
                disabled: true,
            },
            {
                id: 'm2',
                label: 'Milestone 2',
                sublabel: 'Design approved',
                icon: <FileText size={28} weight="duotone" />,
                onClick: () => setQuotesOpen(true),
                disabled: true,
            },
            {
                id: 'm3',
                label: 'Milestone 3',
                sublabel: 'Permit submitted',
                icon: <FileText size={28} weight="duotone" />,
                onClick: () => setQuotesOpen(true),
                disabled: true,
            },
            ];
            if (hasStripeAccess) {
                cards.push({
                    id: 'stripe',
                    label: 'Stripe',
                    sublabel: stripeStatus.sublabel,
                    complete: stripeStatus.complete,
                    badgeTone: stripeStatus.badgeTone,
                    icon: <CreditCard size={28} weight="duotone" />,
                    onClick: () => {
                        const blockMessage = stripeFinancePartnerSyncMessage(
                            opportunityDetails,
                            financePartnersList,
                            getActiveMilestoneProducts(propSavedProducts, opportunityDetails),
                        );
                        if (blockMessage) {
                            showStripeFinancePartnerSyncModal(blockMessage, theme);
                            return;
                        }
                        setStripeOpen(true);
                    },
                });
            }
            if (documentsAccessEnabled) {
                cards.push({
                    id: 'documents',
                    label: 'Documents',
                    sublabel: 'Manage',
                    icon: <Files size={28} weight="duotone" />,
                    onClick: () => setDocumentsModalOpen(true),
                    disabled: false,
                    hideTopRightBadge: true,
                });
            }
            return cards;
        },
        [
            checklistProgress,
            checklistResponseTone,
            financeAgreement,
            financeBadgesAndStatus,
            combinedPackBadgesAndStatus,
            salesforceBadgesAndStatus,
            salesForceId,
            salesRepChecklistEnabled,
            documentsAccessEnabled,
            setAgreementsOpen,
            setQuotesOpen,
            setDocumentsModalOpen,
            setSalesRepChecklistModalOpen,
            setStripeOpen,
            hasStripeAccess,
            stripeStatus,
            opportunityDetails,
            financePartnersList,
            propSavedProducts,
            theme,
        ]
    );
}

export function partnerMilestoneIcon(displayName: string): React.ReactNode {
    const k = String(displayName || '').toLowerCase().replace(/\s+/g, '');
    if (k.includes('lightreach')) return <Sun size={28} weight="duotone" />;
    if (k.includes('goodleap') || (k.includes('good') && k.includes('leap'))) {
        return <CurrencyCircleDollar size={28} weight="duotone" />;
    }
    if (k.startsWith('igs')) return <Buildings size={28} weight="duotone" />;
    if (k.includes('cash')) return <Wallet size={28} weight="duotone" />;
    if (k.includes('battery')) return <BatteryHigh size={28} weight="duotone" />;
    if (k.includes('roof') || k === 'rr' || k === 'rrr') return <House size={28} weight="duotone" />;
    return <CurrencyCircleDollar size={28} weight="duotone" />;
}

export type ObdMilestonesGridProps = {
    theme: 'dark' | 'light';
    cards: ObdMilestoneCardDef[];
    className?: string;
};

export const ObdMilestonesGrid: React.FC<ObdMilestonesGridProps> = ({ theme, cards, className }) => (
    <div
        className={`obd-v3-milestones-grid obd-theme-${theme}${className ? ` ${className}` : ''}`}
        role="list"
    >
        {cards.map((card) => {
            const cardStyle: React.CSSProperties = card.badgeColor
                ? {
                      borderColor: `color-mix(in srgb, ${card.badgeColor} 50%, var(--obd-milestone-border))`,
                      background: `color-mix(in srgb, ${card.badgeColor} 8%, var(--obd-milestone-surface-muted, #f4f6fa))`,
                  }
                : {};
            return (
            <button
                key={card.id}
                type="button"
                className={`obd-v3-milestone-card${card.complete ? ' obd-v3-milestone-card--complete' : ''}${
                    card.disabled ? ' obd-v3-milestone-card--disabled' : ''
                }`}
                style={cardStyle}
                onClick={card.disabled ? undefined : card.onClick}
                disabled={card.disabled}
                role="listitem"
                title={card.disabled ? undefined : card.label}
            >
                {!card.hideTopRightBadge && (
                    <span
                        className={`obd-v3-milestone-card__badge${
                            card.complete || card.badgeTone === 'paid'
                                ? ' obd-v3-milestone-card__badge--done'
                                : card.badgeTone === 'partial'
                                    ? ' obd-v3-milestone-card__badge--partial'
                                    : card.badgeTone === 'unpaid'
                                    ? ' obd-v3-milestone-card__badge--unpaid'
                                    : ''
                        }`}
                    style={card.badgeColor ? { color: card.badgeColor } : undefined}
                        aria-hidden
                    >
                        {card.complete || card.badgeTone === 'paid' ? (
                            <CheckCircle size={16} weight="duotone" />
                        ) : (
                            <Circle size={16} weight={card.badgeTone || card.badgeColor ? 'fill' : 'duotone'} />
                        )}
                    </span>
                )}
                <span className="obd-v3-milestone-card__icon" aria-hidden>
                    {card.icon}
                </span>
                <span className="obd-v3-milestone-card__label">{card.label}</span>
                {card.sublabel ? (
                    <span className="obd-v3-milestone-card__sub">{card.sublabel}</span>
                ) : null}
                {card.badges && (
                    <div className="obd-v3-milestone-card__badges">
                        {card.badges}
                    </div>
                )}
            </button>
        );
    })}
    </div>
);
